import { useCallback, useEffect, useRef, useState } from "react";

import { Options } from "ky";

import { useAPIToken } from "@ip-contexts/api-token-context";
import useFetch from "@ip-utils/use-fetch";

const audience = process.env.REACT_APP_API_CONTRACT_AUDIENCE ?? "";

const permission = {
  audience,
  scope: "super:all",
};

const prefixUrl = process.env.REACT_APP_API_CONTRACT_URL;

export default function useFetchContract() {
  const [isLoading, setIsLoading] = useState(true);
  const {
    state: { tokens, requestAccessToken, renewToken },
  } = useAPIToken();

  const [token, setToken] = useState<any>();

  const tokenRef = useRef<any>();
  tokenRef.current = token;

  const {
    get: getRequest,
    create: createRequest,
    update: updateRequest,
    remove: removeRequest,
  } = useFetch();

  const get = useCallback(
    async (url: string, options?: {}) => {
      return await getRequest(
        url,
        tokenRef.current?.accessToken,
        () => renewToken(permission),
        {
          prefixUrl,
          ...options,
        }
      );
    },
    [getRequest, renewToken]
  );

  const create = async (url: string, payload: any, options: Options = {}) => {
    return await createRequest(
      url,
      payload,
      tokenRef.current?.accessToken,
      () => renewToken(permission),
      {
        prefixUrl,
        ...options,
      }
    );
  };

  const update = async (url: string, payload: any, options: Options = {}) => {
    return await updateRequest(
      url,
      payload,
      tokenRef.current?.accessToken,
      () => renewToken(permission),
      {
        prefixUrl,
        ...options,
      }
    );
  };

  const remove = async (url: string) => {
    return await removeRequest(
      url,
      tokenRef.current?.accessToken,
      () => renewToken(permission),
      {
        prefixUrl,
      }
    );
  };

  useEffect(() => {
    if (tokens[audience]?.accessToken) {
      tokenRef.current = tokens[audience];
      setToken(tokens[audience]);
      setIsLoading(false);
    }
  }, [tokens]);

  useEffect(() => {
    const fetch = async () => {
      requestAccessToken(permission);
    };

    if (!token) {
      fetch();
    }
  }, [requestAccessToken, token]);

  return {
    isLoading,
    error: tokens[audience]?.tokenError,
    get,
    create,
    update,
    remove,
  };
}
